import { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/Button";

// GHL inbound webhook — live endpoint.
export const AUDIT_WEBHOOK_URL = "https://services.leadconnectorhq.com/hooks/XBbWEPnO4deTtZf5rKu7/webhook-trigger/087e63d5-4e1b-4a28-863a-6c5093a8cb25";
// Booking calendar for review/scope calls (GoHighLevel).
export const CALENDAR_URL = "https://flow.theaidept.com/widget/booking/sxwIIw3f28s147Ec4dWP";

const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Copy edits never require logic edits — everything lives here.
// team: "growth" | "workflow" | null (segmentation) | "intent"
// Each option carries a weight `w` (0 = no gap, 3 = biggest gap).
export const AUDIT_CONFIG = {
  questions: [
    {
      id: "segment",
      team: null,
      text: "What best describes your business?",
      options: [
        { label: "Real estate" },
        { label: "Legal" },
        { label: "Healthcare" },
        { label: "Automotive" },
        { label: "Home services" },
        { label: "Other" },
      ],
    },
    {
      id: "afterhours",
      team: "growth",
      text: "What happens when a new lead calls after hours?",
      options: [
        { label: "We miss it", w: 3 },
        { label: "Voicemail", w: 2 },
        { label: "An answering service", w: 1 },
        { label: "It's handled automatically", w: 0 },
      ],
    },
    {
      id: "speed",
      team: "growth",
      text: "How fast does a new lead get a response?",
      options: [
        { label: "Within 5 minutes", w: 0 },
        { label: "Within an hour", w: 1 },
        { label: "Same day", w: 2 },
        { label: "Honestly, it varies", w: 3 },
      ],
    },
    {
      id: "content",
      team: "growth",
      text: "How consistently do you publish content — video, social, email?",
      options: [
        { label: "Weekly or more", w: 0 },
        { label: "Monthly", w: 1 },
        { label: "Rarely", w: 2 },
        { label: "Never, and it shows", w: 3 },
      ],
    },
    {
      id: "followup",
      team: "growth",
      text: "How do you follow up with past clients and old leads?",
      options: [
        { label: "Systematically", w: 0 },
        { label: "Occasionally", w: 1 },
        { label: "When I remember", w: 2 },
        { label: "We don't", w: 3 },
      ],
    },
    {
      id: "admin",
      team: "workflow",
      text: "How much of your week goes to repetitive admin — data entry, scheduling, copying things between tools?",
      options: [
        { label: "Under 2 hours", w: 0 },
        { label: "2–5 hours", w: 1 },
        { label: "5–10 hours", w: 2 },
        { label: "More than 10", w: 3 },
      ],
    },
    {
      id: "tools",
      team: "workflow",
      text: "How many software tools does your business run on?",
      options: [
        { label: "1–3", w: 0 },
        { label: "4–7", w: 1 },
        { label: "8+", w: 2 },
        { label: "I've lost count", w: 3 },
      ],
    },
    {
      id: "handoff",
      team: "intent",
      text: "If you could hand one thing off tomorrow, what would it be?",
      options: [
        { label: "Getting customers", intent: "growth" },
        { label: "Running the systems", intent: "workflow" },
        { label: "Both, honestly", intent: "both" },
      ],
    },
  ],
  // Priority labels named back to the client on the Priority result. Copy only.
  priorities: {
    afterhours: "after-hours lead capture",
    speed: "lead response speed",
    content: "consistent, published content",
    followup: "follow-up and reactivation",
    admin: "repetitive admin",
    tools: "scattered tools and integrations",
  },
  // Tier result copy — edit freely, no logic changes required.
  tiers: {    starter: {
      name: "Priority",
      price: "From $500/mo",
      line: (priority) =>
        `Your priority: ${priority}. Priority puts the department on exactly that.`,
    },
    standard: {
      name: "Full Team",
      price: "$1,500/mo",
      line: () =>
        "Your whole function, run end to end by one team.",
    },
    scale: {
      name: "Full Department — the full department",
      price: "$5,000/mo",
      line: () =>
        "You're past patch fixes. The full department runs your funnel and your operations end to end — book a call to scope it.",
    },
  },
};

const scoreAudit = (answers) => {
  let g = 0, w = 0, gMax = 0, wMax = 0;
  AUDIT_CONFIG.questions.forEach((q) => {
    if (q.team !== "growth" && q.team !== "workflow") return;
    const max = Math.max(...q.options.map((o) => o.w));
    const sel = answers[q.id];
    const val = sel != null ? q.options[sel].w : 0;
    if (q.team === "growth") { g += val; gMax += max; }
    else { w += val; wMax += max; }
  });
  const gNorm = gMax ? g / gMax : 0;
  const wNorm = wMax ? w / wMax : 0;
  const readiness = Math.round(((g + w) / ((gMax + wMax) || 1)) * 100);

  const iq = AUDIT_CONFIG.questions.find((q) => q.id === "handoff");
  const intent = answers.handoff != null ? iq.options[answers.handoff].intent : null;

  const gHigh = gNorm >= 0.5;
  const wHigh = wNorm >= 0.5;

  // Name the dominant constraint from the highest-weighted answered question on a side.
  const topPriority = (side) => {
    const ids = side === "growth" ? ["afterhours", "speed", "content", "followup"] : ["admin", "tools"];
    let bestId = ids[0], bestW = -1;
    ids.forEach((id) => {
      const q = AUDIT_CONFIG.questions.find((x) => x.id === id);
      const sel = answers[id];
      const wv = sel != null ? q.options[sel].w : 0;
      if (wv > bestW) { bestW = wv; bestId = id; }
    });
    return AUDIT_CONFIG.priorities[bestId];
  };

  let tier, side, priority = null;
  if ((gHigh && wHigh) || intent === "both") {
    // Both sides need hands → the full department runs them concurrently.
    tier = "scale"; side = "both";
  } else {
    tier = "starter";
    const eg = gNorm + (intent === "growth" ? 0.1 : 0);
    const ew = wNorm + (intent === "workflow" ? 0.1 : 0);
    side = ew > eg ? "workflow" : "growth";
    priority = topPriority(side);
  }
  const reactivate = [2, 3].includes(answers.followup);
  return { readiness, tier, side, priority, reactivate };
};

// Which team the recommendation names back to the client.
const TEAM_LABELS = {
  growth: "Marketing & creative",
  workflow: "Workflow & automation",
  both: "Marketing & creative + Workflow & automation",
};

const Card = ({ onClick, selected, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-testid={testid}
    className={`flex w-full items-center justify-between gap-4 rounded-2xl border px-6 py-5 text-left text-[16px] font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ember)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-base)] ${
      selected
        ? "border-[var(--ember)] bg-[var(--ember)]/[0.06] text-[var(--ink)]"
        : "border-black/10 bg-[var(--bg-base)] text-[var(--ink)] hover:-translate-y-0.5 hover:border-[var(--ember)]/40"
    }`}
  >
    <span>{children}</span>
    <ArrowRight size={17} className="shrink-0 text-[var(--ember)] opacity-0 transition-opacity group-hover:opacity-100" />
  </button>
);

const inputCls =
  "w-full rounded-xl border border-black/10 bg-[var(--bg-base)] px-4 py-3 text-[15px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ember)] placeholder:text-[var(--muted)]/60";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const AuditQuiz = () => {
  const questions = AUDIT_CONFIG.questions;
  const total = questions.length; // 8
  const [step, setStep] = useState(0); // 0..7 questions, 8 gate, 9 results
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ firstName: "", email: "", phone: "", business: "" });
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  const select = (qid, idx) => {
    setAnswers((a) => ({ ...a, [qid]: idx }));
    const next = () => setStep((s) => Math.min(s + 1, total));
    if (reduced()) next();
    else setTimeout(next, 260);
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submitLead = async (e) => {
    e.preventDefault();
    const er = {};
    if (!lead.firstName.trim()) er.firstName = "Please add your first name.";
    if (!emailOk(lead.email)) er.email = "Please add a valid email.";
    if (!lead.business.trim()) er.business = "Please add your business name.";
    setErrors(er);
    if (Object.keys(er).length) return;

    const scored = scoreAudit(answers);
    const answersDetail = questions.map((q) => ({
      id: q.id,
      question: q.text,
      answer: answers[q.id] != null ? q.options[answers[q.id]].label : null,
    }));
    const answersSummary = answersDetail
      .map((a) => `${a.question}: ${a.answer || "—"}`)
      .join(" | ");
    // Flat top-level fields so GoHighLevel auto-maps the contact; detail kept alongside.
    const payload = {
      source: "readiness-check",
      firstName: lead.firstName,
      email: lead.email,
      phone: lead.phone,
      business: lead.business,
      readiness_score: scored.readiness,
      recommended_tier: scored.tier,
      recommended_team: TEAM_LABELS[scored.side] || "",
      priority: scored.priority || "",
      answers_summary: answersSummary,
      answers: answersDetail,
      result: scored,
    };
    try {
      await fetch(AUDIT_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch {
      /* results still render locally regardless of webhook state */
    }
    setResult(scored);
    setStep(total + 1);
  };

  // ---- Confirmation (no score shown) ----
  if (step === total + 1 && result) {
    return (
      <div data-testid="audit-results">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">You're all set</p>
        <h3 className="mt-6 t-section" data-testid="audit-confirmation-heading">
          Thanks{lead.firstName ? `, ${lead.firstName}` : ""}. We've got your details.
        </h3>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[var(--muted)]" data-testid="audit-confirmation-blurb">
          A specialist will review your answers and reach out shortly with your free
          Business DNA audit — six dimensions deep, with a written build plan. No
          charge, no obligation. Want to move faster? Book your call now.
        </p>

        <div className="mt-8 rounded-2xl border border-black/10 bg-[var(--bg-contrast)] p-6">
          <Button href={CALENDAR_URL} variant="primary" data-testid="audit-dna-link">
            Book my free Business DNA audit
          </Button>
          <p className="mt-3 text-sm text-[var(--green-ink)]">
            Free Business DNA audit. No charge, no obligation.
          </p>
        </div>
      </div>
    );
  }

  // ---- Lead gate ----
  if (step === total) {
    return (
      <div data-testid="audit-lead-gate">
        <div className="flex items-center gap-4">
          <button onClick={back} className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--muted)] transition-colors hover:text-[var(--ember)]" data-testid="audit-back">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
        <h3 className="mt-6 text-2xl font-semibold leading-tight tracking-[-0.02em] sm:text-3xl">
          How can we contact you?
        </h3>
        <form className="mt-8 space-y-5" onSubmit={submitLead} noValidate data-testid="audit-lead-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="fname">First name</label>
              <input id="fname" className={inputCls} value={lead.firstName} onChange={(e) => setLead({ ...lead, firstName: e.target.value })} data-testid="audit-firstname" />
              {errors.firstName && <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.firstName}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="bname">Business name</label>
              <input id="bname" className={inputCls} value={lead.business} onChange={(e) => setLead({ ...lead, business: e.target.value })} data-testid="audit-business" />
              {errors.business && <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.business}</p>}
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="aemail">Email</label>
              <input id="aemail" type="email" className={inputCls} value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="audit-email" />
              {errors.email && <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.email}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="aphone">Phone (optional)</label>
              <input id="aphone" className={inputCls} value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="audit-phone" />
            </div>
          </div>
          <Button type="submit" variant="primary" data-testid="audit-show-results">Continue</Button>
        </form>
      </div>
    );
  }

  // ---- Question screens ----
  const q = questions[step];
  const pct = ((step + 1) / total) * 100;

  return (
    <div data-testid="audit-quiz">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted)]" data-testid="audit-progress">
          Question {step + 1} of {total}
        </span>
        {step > 0 && (
          <button onClick={back} className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--muted)] transition-colors hover:text-[var(--ember)]" data-testid="audit-back">
            <ArrowLeft size={16} /> Back
          </button>
        )}
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-black/10" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={total}>
        <div className="h-full rounded-full bg-[var(--ember)] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <h3 className="mt-8 text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-3xl" data-testid="audit-question">
        {q.text}
      </h3>

      <div className="group/list mt-8 grid gap-3">
        {q.options.map((o, i) => (
          <div key={o.label} className="group">
            <Card
              onClick={() => select(q.id, i)}
              selected={answers[q.id] === i}
              testid={`audit-option-${step}-${i}`}
            >
              {o.label}
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};
